import React, { useState } from 'react';
import axios from 'axios';
import { Download, Copy, Mail, Loader2, Check, X } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * "Send this daily deliverable" fallback (no email backend yet).
 *
 * Renders as a small, self-contained toolbar. Clicking:
 *   · Preview   → opens the HTML preview in a new tab
 *   · Download  → triggers the PDF download
 *   · Copy HTML → copies the deliverable HTML to the clipboard so the
 *                 operator can paste directly into Gmail (which will
 *                 render the styled table verbatim)
 *   · Compose   → opens the default mail client with a pre-filled
 *                 subject + placeholder body pointing at the preview URL
 *
 * The whole thing works over the two existing operator endpoints:
 *   GET /api/daily-recon/{cid}/deliverable/preview
 *   GET /api/daily-recon/{cid}/deliverable/pdf
 *
 * When Resend / Slack are wired later, this component's `<Button>Send</Button>`
 * slot will call `POST /api/daily-recon/{cid}/deliverable/send` and the
 * rest of the UI stays exactly the same.
 */
export const SendDeliverable = ({ clientId, clientName, businessDate }) => {
  const [busy, setBusy] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  if (!clientId || !businessDate) return null;

  const previewUrl = `${API}/daily-recon/${clientId}/deliverable/preview?business_date=${businessDate}`;
  const pdfUrl     = `${API}/daily-recon/${clientId}/deliverable/pdf?business_date=${businessDate}`;

  const withAuth = () => {
    const token = localStorage.getItem('token') || localStorage.getItem('access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const openPreview = async () => {
    setBusy('preview'); setError('');
    try {
      const { data } = await axios.get(previewUrl, { headers: withAuth(), responseType: 'text' });
      const w = window.open('', '_blank');
      if (w) { w.document.write(data); w.document.close(); }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load preview');
    } finally {
      setBusy(null);
    }
  };

  const downloadPdf = async () => {
    setBusy('pdf'); setError('');
    try {
      const res = await axios.get(pdfUrl, { headers: withAuth(), responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `daily-recon-${businessDate}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to download PDF');
    } finally {
      setBusy(null);
    }
  };

  const copyHtml = async () => {
    setBusy('copy'); setError('');
    try {
      const { data } = await axios.get(previewUrl, { headers: withAuth(), responseType: 'text' });
      // Copy as both text/html and text/plain so Gmail keeps the styled
      // table when pasted into the compose window.
      if (navigator.clipboard && window.ClipboardItem) {
        const blobHtml  = new Blob([data], { type: 'text/html' });
        const blobPlain = new Blob([data.replace(/<[^>]+>/g, '')], { type: 'text/plain' });
        await navigator.clipboard.write([
          new ClipboardItem({ 'text/html': blobHtml, 'text/plain': blobPlain }),
        ]);
      } else {
        await navigator.clipboard.writeText(data);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      setError('Clipboard blocked — try Download PDF and attach manually.');
    } finally {
      setBusy(null);
    }
  };

  const composeMail = () => {
    const subject = `Daily reconciliation · ${clientName || clientId} · ${businessDate}`;
    const body =
      `Attaching today's five-channel daily reconciliation for ${clientName || clientId} (${businessDate}).\n\n` +
      `Every rupee traces to a specific bill, batch, UTR or manager cash-close. ` +
      `Preview and drill-down live in the app.\n\n` +
      `— Epicurea Nexus`;
    const to = '';                              // wire when Client has owner_email on file
    const s  = encodeURIComponent(subject);
    const b  = encodeURIComponent(body);
    window.location.href = `mailto:${to}?subject=${s}&body=${b}`;
  };

  const btn = (id, onClick, Icon, label, testid) => (
    <Button
      onClick={onClick}
      disabled={busy !== null}
      size="sm"
      variant="outline"
      className="text-xs"
      data-testid={testid}
    >
      {busy === id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />}
      <span className="ml-1">{label}</span>
    </Button>
  );

  return (
    <div className="inline-flex flex-wrap items-center gap-2" data-testid="send-deliverable">
      {btn('preview', openPreview, Mail, 'Preview', 'send-deliverable-preview')}
      {btn('pdf',     downloadPdf, Download, 'Download PDF', 'send-deliverable-pdf')}
      <Button
        onClick={copyHtml}
        disabled={busy !== null}
        size="sm"
        variant="outline"
        className="text-xs"
        data-testid="send-deliverable-copy"
      >
        {busy === 'copy' ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
          : copied ? <Check className="h-3.5 w-3.5 text-state-success" />
          : <Copy className="h-3.5 w-3.5" />}
        <span className="ml-1">{copied ? 'Copied' : 'Copy for Gmail'}</span>
      </Button>
      {btn('mail', composeMail, Mail, 'Compose email', 'send-deliverable-compose')}
      {error && (
        <span className="text-xs text-state-error inline-flex items-center gap-1">
          <X className="h-3.5 w-3.5" />{error}
        </span>
      )}
    </div>
  );
};

export default SendDeliverable;
